import React, { useEffect, useState } from 'react'
import { FormikHelpers } from 'formik'

import { LoginHeading } from '@ally/ally-login'
import { Forms } from '@ally/forms'
import {
  Box,
  Button,
  Dejargonator,
  Space,
  TextBody,
  upToMd,
} from '@ally/metronome-ui'
import { OTPMethod, SetupSecurityProps } from '@ally/transmitigator'
import { useAriaLive } from '@ally/use-aria-live'

import { useHostServices } from '../../../HostServices'
import { MessageAndDataModal, SupportedCarriersModal } from '../../../Modals'
import { useTransmit } from '../../../TransmitContext'
import { ErrorBanner } from '../../ErrorBanner'
import { DeliveryOptions } from './DeliveryOptions'
import { PhoneNumberForm } from './PhoneNumberForm'

/**
 * VoIP Dejarg
 */
const voipDejargBody =
  "Voice over Internet Protocol numbers are connected to an IP address instead of to a phone. Because someone else could access your VoIP line from any device, we don't use them for security codes."

const VoipDejarg: React.FC = () => (
  <Dejargonator
    ariaLabelPopover="Select to learn what VoIP is"
    heading="Voice over Internet Protocol (VoIP)"
    body={voipDejargBody}
    trigger={{
      variant: 'text',
      content: 'VoIP',
      ariaDescription: 'Info about VoIP',
    }}
  />
)

/**
 * Copy for Setup Security
 */
interface SetupSecurityCopyProps {
  shouldConfirmOptions: boolean
}

const SetupSecurityCopy: React.FC<SetupSecurityCopyProps> = ({
  shouldConfirmOptions,
}) => {
  if (shouldConfirmOptions) {
    return (
      <Space mb="sm">
        <TextBody size="sm" tag="p" data-testid="security-confirm-copy">
          It&apos;s time to update your security code delivery options. You can
          receive codes through a text or phone call. For security reasons, we
          don&apos;t accept <VoipDejarg /> numbers for verification.
        </TextBody>
      </Space>
    )
  }

  return (
    <Space mb="sm">
      <TextBody size="sm" tag="p" data-testid="security-setup-copy">
        Security codes are an easy way to protect your account — and when we
        need extra verification, it&apos;ll only take a moment. You&apos;ll
        choose each time whether you want to receive the code through a text or
        a phone call. For security reasons, we don&apos;t accept <VoipDejarg />{' '}
        numbers (like Google Voice&#8482;) for verification.
      </TextBody>
    </Space>
  )
}

const sortOptionsByLastFour = (options: OTPMethod[]): OTPMethod[] => {
  return options.sort((a, b) => {
    // Sort by last 4 digits of phone number
    return a.value.slice(-4) < b.value.slice(-4) ? -1 : 1
  })
}

/**
 * Setup Security Component
 */
export const SetupSecurityContent: React.FC<SetupSecurityProps> = ({
  onComplete,
  status,
  options = [],
  shouldConfirmOptions,
}) => {
  const { announceAssertive } = useAriaLive()
  const [pendingOptions, setPendingOptions] = useState<OTPMethod[]>([
    ...sortOptionsByLastFour(options),
  ])
  const [hasDeliveryOptions, setHasDeliveryOptions] = useState<boolean>(
    options.length > 0,
  )
  const [canAddDeliveryOptions, setCanAddDeliveryOptions] = useState<boolean>(
    options.length < 6,
  )
  const [clientError, setClientError] = useState<string>('')

  const [isOptionRemoved, setIsOptionRemoved] = useState(false)

  useEffect(() => {
    setHasDeliveryOptions(pendingOptions.length > 0)
    setCanAddDeliveryOptions(pendingOptions.length < 6)
  }, [pendingOptions])

  const handleSubmit = (): void => {
    setClientError('')

    onComplete(pendingOptions)
  }

  const handleAddNewOptionSubmit = (
    {
      phoneNumber,
    }: {
      phoneNumber: string
    },
    { resetForm }: FormikHelpers<any>,
  ): void => {
    const newOptions = [...pendingOptions]

    newOptions.push({
      type: 'SMS',
      value: phoneNumber,
    })

    setPendingOptions(newOptions)

    announceAssertive('Delivery option successfully added.')

    resetForm()
  }

  const handleIsRemovedOption = (value: boolean): void => {
    setIsOptionRemoved(value)
  }

  const handleRemoveOption = (index: number): void => {
    if (pendingOptions.length === 1) {
      setClientError(
        'You must have at least one delivery option available. Please add another delivery option before removing this one.',
      )
      return
    }

    const newOptions = [...pendingOptions]

    newOptions.splice(index, 1)

    setIsOptionRemoved(true)

    setPendingOptions(newOptions)

    announceAssertive('Delivery option successfully removed.')
  }

  const FORM_ID = 'setup-security-add-option-form'
  const initialValues = {
    phoneNumber: '',
  }

  let header = 'Security code setup'
  if (shouldConfirmOptions) {
    header = 'Confirm security code delivery options'
  }

  const { analytics } = useHostServices()

  React.useEffect(() => {
    analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Security Setup',
    })
  }, [])

  return (
    <>
      <LoginHeading title={header} />

      <Box display="flex" width={upToMd(12, 7)} flexDirection="column">
        <SetupSecurityCopy shouldConfirmOptions={shouldConfirmOptions} />

        {clientError && <ErrorBanner>{clientError}</ErrorBanner>}

        {hasDeliveryOptions && (
          <DeliveryOptions
            removeOption={handleRemoveOption}
            options={pendingOptions}
          />
        )}

        {canAddDeliveryOptions && (
          <Forms
            formId={FORM_ID}
            initialValues={initialValues}
            onSubmit={handleAddNewOptionSubmit}
            validateOnChange={false}
          >
            <PhoneNumberForm
              formId={FORM_ID}
              hasDeliveryOptions={hasDeliveryOptions}
              otpMethods={pendingOptions}
              isOptionRemoved={isOptionRemoved}
              handleIsRemovedOption={handleIsRemovedOption}
            />
          </Forms>
        )}

        <Box mt="xs">
          <MessageAndDataModal />
        </Box>

        <Box mt="xs">
          <SupportedCarriersModal />
        </Box>

        {hasDeliveryOptions && (
          <Button
            mt="lg"
            type="submit"
            variant="primary"
            onClick={handleSubmit}
            text="Continue"
            loading={status === 'Loading'}
          />
        )}
      </Box>
    </>
  )
}

export const SetupSecurity: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'SetupSecurity') {
    return null
  }

  return (
    <SetupSecurityContent
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...activeScreen.payload}
    />
  )
}
